import { GtcrnWorkletNode, loadGtcrn, loadRnnoise, RnnoiseWorkletNode } from '@sapphi-red/web-noise-suppressor';
import gtcrnWasm from '@sapphi-red/web-noise-suppressor/gtcrn.wasm?url';
import gtcrnWorklet from '@sapphi-red/web-noise-suppressor/gtcrnWorklet.js?url';
import rnnoiseSimdWasm from '@sapphi-red/web-noise-suppressor/rnnoise_simd.wasm?url';
import rnnoiseWasm from '@sapphi-red/web-noise-suppressor/rnnoise.wasm?url';
import rnnoiseWorklet from '@sapphi-red/web-noise-suppressor/rnnoiseWorklet.js?url';
import type { DeepFilterNetAudioWorkletOptions } from '@workadventure/noise-suppression/deepfilternet';
import { TrackEvent, type AudioProcessorOptions, type Track, type TrackProcessor } from 'livekit-client';
import howlWorklet from './howl-worklet.ts?worker&url';

export type DenoiseModel = 'deepfilter' | 'rnnoise' | 'gtcrn';

/**
 * DeepFilterNet3's settings; starting values, to tune once the team has used it.
 * - speechAttenuationDb: the most the model takes off while someone speaks (more sounds metallic between words).
 * - pauseAttenuationDb: what a gate after the model takes off in pauses.
 * - pauseGateLookaheadFrames: 10 ms frames the gate looks ahead, so it is open when a word starts (adds 30 ms).
 * - minSpeechFrames: frames of speech the gate waits for before opening, so a single keystroke does not open it.
 */
const DEEPFILTER_OPTIONS = {
  speechAttenuationDb: 35,
  pauseAttenuationDb: 50,
  pauseGateLookaheadFrames: 3,
  minSpeechFrames: 2,
} as const satisfies DeepFilterNetAudioWorkletOptions;

/** The models work on 48 kHz frames; one context for every mic track (DeepFilterNet3 gets its own, see createDenoiser). */
let shared: AudioContext | null = null;
export function denoiseContext(): AudioContext {
  shared ??= new AudioContext({ sampleRate: 48_000 });
  return shared;
}

const binaries = new Map<'rnnoise' | 'gtcrn', Promise<ArrayBuffer>>();
const modules = new WeakMap<BaseAudioContext, Set<string>>();

/** Each worklet module once per context. */
async function addModule(context: BaseAudioContext, url: string) {
  const loaded = modules.get(context) ?? new Set<string>();
  modules.set(context, loaded);
  if (loaded.has(url)) return;
  await context.audioWorklet.addModule(url);
  loaded.add(url);
}

/** A model's node, loaded and ready, and how to let it go. */
interface ModelNode {
  node: AudioNode;
  dispose(): void;
}

async function createModelNode(context: AudioContext, model: DenoiseModel): Promise<ModelNode> {
  if (model === 'deepfilter') {
    // Its own chunk: the code, the Wasm and the model (about 11 MB together) only load once this mode is used.
    const { createDeepFilterNetAudioWorklet } = await import('@workadventure/noise-suppression/deepfilternet');
    const handle = await createDeepFilterNetAudioWorklet(context, DEEPFILTER_OPTIONS);
    try {
      await handle.ready;
    } catch (error) {
      handle.dispose();
      throw error;
    }
    return handle;
  }
  if (!binaries.has(model)) {
    binaries.set(model, model === 'rnnoise' ? loadRnnoise({ url: rnnoiseWasm, simdUrl: rnnoiseSimdWasm }) : loadGtcrn({ url: gtcrnWasm }));
  }
  await addModule(context, model === 'rnnoise' ? rnnoiseWorklet : gtcrnWorklet);
  const wasmBinary = await binaries.get(model)!;
  const node = model === 'rnnoise' ? new RnnoiseWorkletNode(context, { maxChannels: 1, wasmBinary }) : new GtcrnWorkletNode(context, { maxChannels: 1, wasmBinary });
  return {
    node,
    dispose() {
      node.disconnect();
      node.destroy();
    },
  };
}

/** The model, then the howl guard; a source connects to `input`, the cleaned sound comes out of `output`. */
export interface DenoiseChain {
  input: AudioNode;
  output: AudioNode;
  dispose(): void;
}

/**
 * The model followed by the howl guard (feedback between two speakers; packages/client/src/howl.ts). Also used
 * on its own, e.g. to measure the models offline.
 */
export async function createDenoiseChain(context: AudioContext, model: DenoiseModel): Promise<DenoiseChain> {
  await addModule(context, howlWorklet);
  const modelNode = await createModelNode(context, model);
  const guard = new AudioWorkletNode(context, 'novachat-howl-guard', { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1] });
  modelNode.node.connect(guard);
  return {
    input: modelNode.node,
    output: guard,
    dispose() {
      modelNode.dispose();
      guard.disconnect();
      guard.port.close();
    },
  };
}

/**
 * Machine-learning noise suppression in the browser (design §10: Krisp only exists on LiveKit
 * Cloud). The mic track runs through the model, then the howl guard, before LiveKit publishes it.
 *
 * DeepFilterNet3 runs in an AudioContext of its own that is closed with it: its model state stays
 * in the worklet's memory until the context closes, so reusing the shared one would keep a copy
 * for every call and mode change.
 */
export function createDenoiser(model: DenoiseModel): TrackProcessor<Track.Kind.Audio, AudioProcessorOptions> {
  let running: { source: MediaStreamAudioSourceNode; chain: DenoiseChain; own: AudioContext | null; unfollow(): void } | null = null;
  /** Counts inits and destroys, so a destroy during a slow first download still wins. */
  let generation = 0;

  const processor: TrackProcessor<Track.Kind.Audio, AudioProcessorOptions> = {
    name: `novachat-${model}`,
    async init({ track, audioContext, localTrack }) {
      const current = ++generation;
      const own = model === 'deepfilter' ? new AudioContext({ sampleRate: 48_000 }) : null;
      void own?.resume().catch(() => undefined);
      const context = own ?? audioContext;
      let chain: DenoiseChain;
      try {
        chain = await createDenoiseChain(context, model);
      } catch (error) {
        void own?.close().catch(() => undefined);
        throw error;
      }
      if (current !== generation) {
        chain.dispose();
        void own?.close().catch(() => undefined);
        return;
      }
      const source = context.createMediaStreamSource(new MediaStream([track]));
      const destination = context.createMediaStreamDestination();
      chain.output.connect(destination);
      // While the mic is muted the model gets no input and skips its work (each model's worklet does).
      const pause = () => source.disconnect();
      const play = () => source.connect(chain.input);
      localTrack?.on(TrackEvent.Muted, pause).on(TrackEvent.Unmuted, play);
      if (!localTrack?.isMuted) play();
      running = { source, chain, own, unfollow: () => localTrack?.off(TrackEvent.Muted, pause).off(TrackEvent.Unmuted, play) };
      processor.processedTrack = destination.stream.getAudioTracks()[0];
    },
    async restart(options) {
      await processor.destroy();
      await processor.init(options);
    },
    async destroy() {
      generation++;
      if (!running) return;
      const { source, chain, own, unfollow } = running;
      running = null;
      unfollow();
      source.disconnect();
      chain.dispose();
      await own?.close().catch(() => undefined);
    },
  };
  return processor;
}
